import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { sendApiRequest } from "../utils";

const UpdateKYC = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [kycHistory, setKycHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const userData = useSelector(state => state.user_info[0]?.user);

  // Fetch KYC history
  useEffect(() => {
    const fetchKYCHistory = async () => {
      try {
        const response = await sendApiRequest(`api/kyc/history/${userData._id}`, {},"GET");
        if (response.data.success) {
          setKycHistory(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching KYC history:', error);
      }
    };

    fetchKYCHistory();
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Upload images to Cloudinary first
      const uploadImage = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'prayagproperties');

        const cloudinaryResponse = await fetch(
          'https://api.cloudinary.com/v1_1/djmyigser/image/upload',
          {
            method: 'POST',
            body: formData
          }
        );

        const cloudinaryData = await cloudinaryResponse.json();
        if (!cloudinaryData.secure_url) {
          throw new Error('Failed to upload image to Cloudinary');
        }
        return cloudinaryData.secure_url;
      };

      // Upload all images
      const [panUrl, aadhaarUrl, chequeUrl] = await Promise.all([
        uploadImage(data.panScanCopy[0]),
        uploadImage(data.aadhaarScanCopy[0]),
        uploadImage(data.cancelChequeCopy[0])
      ]);

      // Send KYC data to backend
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/kyc/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          panNo: data.panNo,
          aadhaarNo: data.aadhaarNo,
          panScanCopy: panUrl,
          aadhaarScanCopy: aadhaarUrl,
          cancelChequeCopy: chequeUrl
        })
      });

      const result = await response.json();

      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'KYC details updated successfully'
        });
        reset();
        // Refresh KYC history
        const historyResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/kyc/history/${userData._id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const historyData = await historyResponse.json();
        if (historyData.success) {
          setKycHistory(historyData.data);
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: result.message || 'Failed to update KYC details'
        });
      }
    } catch (error) {
      console.error('Error updating KYC:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while updating KYC details'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#49c2c7]/5 via-[#25b2da]/10 to-[#0d61b5]/5 py-4 sm:py-4 md:py-1">
      <div className="container mx-auto px-2 sm:px-2 md:px-1 max-w-4xl">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl overflow-hidden border border-[#25b2da]/30">
          <div className="bg-gradient-to-r from-[#13407e] via-[#0d61b5] to-[#25b2da] text-white px-4 sm:px-6 py-4 sm:py-5 flex justify-between items-center">
            <h1 className="text-lg sm:text-xl font-semibold">Update KYC</h1>
            <div className="flex space-x-1.5 sm:space-x-2">
              <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#2ba974] shadow-inner"></span>
              <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#21783b] shadow-inner"></span>
              <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#49c2c7] shadow-inner"></span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
            {/* Personal Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 bg-[#49c2c7]/5 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-[#25b2da]/20">
              <div className="md:col-span-2">
                <h2 className="text-base sm:text-lg font-semibold text-[#13407e] mb-3 sm:mb-4">Personal Information</h2>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData?.fullName}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  Mobile <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData?.phone}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#13407e] mb-2">
                  User ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={userData?.sponsorCode}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-gray-500"
                />
              </div>
            </div>



            {/* KYC Documents */}
            <div className="space-y-4 sm:space-y-6 bg-[#25b2da]/5 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-[#25b2da]/20">
              <h2 className="text-base sm:text-lg font-semibold text-[#13407e] mb-3 sm:mb-4">KYC Documents</h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#13407e] mb-2">
                      PAN No. <span className="text-[#21783b]">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("panNo", { required: "PAN number is required" })}
                      className="w-full px-4 py-3 rounded-lg border border-[#49c2c7]/30 focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-300 outline-none bg-white/50"
                      placeholder="Enter PAN number"
                    />
                    {errors.panNo && (
                      <p className="mt-2 text-sm text-[#21783b]">{errors.panNo.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#13407e] mb-2">
                      PAN Scan Copy <span className="text-[#21783b]">*</span>
                    </label>
                    <div className="relative group">
                      <input
                        type="file"
                        {...register("panScanCopy")}
                        className="w-full px-4 py-3 rounded-lg border border-[#49c2c7]/30 focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-300 outline-none bg-white/50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#25b2da]/10 file:text-[#13407e] hover:file:bg-[#25b2da]/20"
                      />
                    </div>
                  </div>
                </div>

                {/* Similar styling for Aadhaar and Cancel Cheque sections */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#13407e] mb-2">
                      Aadhaar No. <span className="text-[#21783b]">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("aadhaarNo", { required: "Aadhaar number is required" })}
                      className="w-full px-4 py-3 rounded-lg border border-[#49c2c7]/30 focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-300 outline-none bg-white/50"
                      placeholder="Enter Aadhaar number"
                    />
                    {errors.aadhaarNo && (
                      <p className="mt-2 text-sm text-[#21783b]">{errors.aadhaarNo.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#13407e] mb-2">
                      Aadhaar Scan Copy <span className="text-[#21783b]">*</span>
                    </label>
                    <div className="relative group">
                      <input
                        type="file"
                        {...register("aadhaarScanCopy")}
                        className="w-full px-4 py-3 rounded-lg border border-[#49c2c7]/30 focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-300 outline-none bg-white/50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#25b2da]/10 file:text-[#13407e] hover:file:bg-[#25b2da]/20"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#13407e] mb-2">
                      Cancel Cheque Copy <span className="text-[#21783b]">*</span>
                    </label>
                    <div className="relative group">
                      <input
                        type="file"
                        {...register("cancelChequeCopy")}
                        className="w-full px-4 py-3 rounded-lg border border-[#49c2c7]/30 focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-300 outline-none bg-white/50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#25b2da]/10 file:text-[#13407e] hover:file:bg-[#25b2da]/20"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2 sm:pt-4">
              <button
                type="submit"
                className="w-full sm:w-auto bg-gradient-to-r from-[#13407e] via-[#0d61b5] to-[#25b2da] text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg 
                hover:from-[#0d61b5] hover:via-[#25b2da] hover:to-[#13407e] transition-all duration-500 transform hover:scale-105 
                focus:ring-4 focus:ring-[#25b2da]/20 shadow-md hover:shadow-lg active:scale-95 text-sm sm:text-base"
              >
                Update KYC
              </button>
            </div>
          </form>

          {/* KYC History Table */}
          {/* KYC History Section */}
          <div className="mt-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">KYC History</h2>
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PAN No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aadhaar No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {kycHistory.length > 0 ? (
                    kycHistory.map((kyc) => (
                      <tr key={kyc._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(kyc.createdAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{kyc.panNo}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{kyc.aadhaarNo}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${kyc.status === 'Approved' ? 'bg-green-100 text-green-800' : kyc.status === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {kyc.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex space-x-3">
                            <a
                              href={kyc.panScanCopy}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                            >
                              <i className="fas fa-file-alt mr-1"></i> PAN
                            </a>
                            <a
                              href={kyc.aadhaarScanCopy}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                            >
                              <i className="fas fa-file-alt mr-1"></i> Aadhaar
                            </a>
                            <a
                              href={kyc.cancelChequeCopy}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-900 flex items-center"
                            >
                              <i className="fas fa-file-alt mr-1"></i> Cheque
                            </a>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {kyc.remarks || '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                        No KYC history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateKYC;